import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";

import { DeviceEnum } from "../../types/context-detection";
import { DeviceScrollEnum } from "../../types/suggestions";
import { IHandleKeyboardNavigation } from "../../types/keyboard-navigation";
import { ISuggestionResponse } from "../../types/suggestion-response";
import { SearchBoxProps } from "../../types/searchbox";

import { L10nContext, NavigationContext } from "../common/contexts";
import { PageViewLogDataId } from "../../util/pageViewLogDataId";
import { useL10n } from "../common/l10n";
import { useKeyboardNavigation } from "../common/keyboard-navigation";
import debounce from "../../util/debounce";
import defaultSuggestionResponse from "../../util/defaultSuggestionResponse";
import SearchIcon from "../../Assets/svg/Search_2021_icon";
import SuggestionList from "../suggestion-list/suggestion-list";
import SuggestionColumns from "../suggestion-list/suggestion-columns";
import useSuggestionsRenderView from "../suggestion-list/suggestions-render-view";

import "./searchbox-flyout.scss";
import "../suggestion-list/suggestions-flyout.scss";

const SearchBoxFlyout = ({ detectedContext, ...props }: SearchBoxProps) => {
    const { isWebShop, isMobileShop, device, customerType } = detectedContext;
    const SEARCH_TERM_LENGTH_LIMIT = 50;
    const TOP_SEARCH_QUERIES_LIMIT = isWebShop ? 10 : 5;
    const showColumns = useSuggestionsRenderView("flyout", isMobileShop);

    const [isOpen, setIsOpen] = useState(false);
    const [focus, setFocus] = useState(false);
    const [input, setInput] = useState("");
    const [validSearchTerm, setValidSearchTerm] = useState("");
    const [suggestions, setSuggestions] = useState<ISuggestionResponse>(
        defaultSuggestionResponse()
    );
    const [hasSuggestions, setHasSuggestions] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const searchBoxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const focusRef = useRef(focus);
    const abortControllerRef = useRef<AbortController | null>(null);
    const keyboardNavColumnElementRef = useRef<IHandleKeyboardNavigation | null>(null);
    const keyboardNavListElementRef = useRef<IHandleKeyboardNavigation | null>(null);

    const pageViewLogDataId = PageViewLogDataId.get();

    const l10n = useL10n(props.l10n);

    useEffect(() => {
        focusRef.current = focus;
    }, [focus]);

    useEffect(() => {
        const hasSuggestions =
            suggestions.articles.length > 0 ||
            suggestions.flexibleSets.length > 0 ||
            suggestions.contents.length > 0 ||
            suggestions.topSearchQueries.length > 0;
        setHasSuggestions(hasSuggestions);
    }, [suggestions]);

    useEffect(() => {
        const showSuggestions = hasSuggestions && isOpen && !!validSearchTerm;
        setShowSuggestions(showSuggestions);
    }, [hasSuggestions, isOpen, validSearchTerm]);

    const handleClick = useCallback((event: Event) => {
        const target = event.target as HTMLElement;

        // exclude clicks on searchbox and on mkt header
        const removeFocus =
            focusRef.current &&
            !searchBoxRef.current?.contains(target) &&
            !target.closest(".mkt-headr-content");

        if (removeFocus) {
            handleBlur();
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClick);

        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [handleClick]);

    useEffect(() => {
        shell?.messageBroker.subscribeTo(
            "ESMKT.HeadR.FlyoutOpened",
            (data: unknown) => {
                const payload = data as string;
                if (payload === "SearchBoxFlyout") onFlyoutOpened();
            },
            "ESMKT.HeadR.FlyoutOpened"
        );

        shell?.messageBroker.subscribeTo(
            "ESMKT.HeadR.FlyoutClosed",
            (data: unknown) => {
                const payload = data as string;
                if (payload === "SearchBoxFlyout") onFlyoutClosed();
            },
            "ESMKT.HeadR.FlyoutClosed"
        );

        return () => {
            shell?.messageBroker.unsubscribeFrom(
                "ESMKT.HeadR.FlyoutOpened",
                "ESMKT.HeadR.FlyoutOpened"
            );
            shell?.messageBroker.unsubscribeFrom(
                "ESMKT.HeadR.FlyoutClosed",
                "ESMKT.HeadR.FlyoutClosed"
            );
        };
    }, []);

    const toggleFlyoutTriggeredByTabNavigation = useCallback((event: KeyboardEvent) => {
        if (event.key === "Enter") {
            shell?.messageBroker.publishTo("ESMKT.HeadR.ToggleFlyout", "SearchBoxFlyout");
        } else if (event.shiftKey && event.key === "Tab") {
            closeFlyoutTriggeredByTabNavigation();
        }
    }, []);

    useEffect(() => {
        const iconWrapper = document.querySelector<HTMLElement>(
            ".scope-ssw.mkt-headr-icon-wrapper"
        );
        const flyoutIcon = iconWrapper?.querySelector<HTMLElement>(".ssw-flyout-icon");
        if (flyoutIcon) {
            flyoutIcon.addEventListener("keydown", toggleFlyoutTriggeredByTabNavigation);

            return () => {
                flyoutIcon.removeEventListener("keydown", toggleFlyoutTriggeredByTabNavigation);
            };
        }
    }, [toggleFlyoutTriggeredByTabNavigation]);

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement> | KeyboardEvent) => {
            e.preventDefault();

            const trimmedInput = input.trim();
            if (trimmedInput) {
                window.location.href = `${props.searchTarget}?query=${encodeURIComponent(
                    trimmedInput
                )}`;
            }
        },
        [input, props.searchTarget]
    );

    const navigationApi = useKeyboardNavigation(
        showColumns
            ? () => keyboardNavColumnElementRef.current?.getNavigationElements() ?? []
            : () => keyboardNavListElementRef.current?.getNavigationElements() ?? []
    );

    const handleKeyPressedWhileFocused = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Tab" || (event.shiftKey && event.key === "Tab")) {
                closeFlyoutTriggeredByTabNavigation();
            } else if (
                (event.key === "Escape" || event.key === "Esc") &&
                (showColumns
                    ? keyboardNavColumnElementRef.current
                    : keyboardNavListElementRef.current) !== null
            ) {
                shell?.tabNav.focusPrevious();
                closeFlyoutTriggeredByTabNavigation();
            } else if (event.key === "Enter" && !navigationApi.hasSelectedNavElement()) {
                handleSubmit(event);
            } else if (device === DeviceEnum.Desktop) {
                navigationApi.handleKeyDown(event);
            }
        },
        [showColumns, device, navigationApi, handleSubmit]
    );

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;

        if (focus) {
            input.addEventListener("keydown", handleKeyPressedWhileFocused);
        } else {
            input.removeEventListener("keydown", handleKeyPressedWhileFocused);
        }

        return () => {
            input.removeEventListener("keydown", handleKeyPressedWhileFocused);
        };
    }, [focus, handleKeyPressedWhileFocused]);

    useEffect(() => {
        const suggestionsVisible = hasSuggestions && showSuggestions;
        if (!suggestionsVisible) {
            navigationApi.resetNavigation();
        }
    }, [hasSuggestions, showSuggestions, navigationApi]);

    const handleBlur = () => {
        setFocus(false);
    };

    function onFlyoutOpened(): void {
        setIsOpen(true);
        inputRef.current?.toggleAttribute("focusable");

        // might need adjustment for desktop vs isTouchDevice
        setFocus(true);
        inputRef.current?.focus();
    }

    function onFlyoutClosed(): void {
        setIsOpen(false);
        inputRef.current?.toggleAttribute("focusable");

        // might need adjustment for desktop vs isTouchDevice
        setFocus(false);
    }

    function handleOnFocusFallback(event: React.FocusEvent<HTMLInputElement>): void {
        const refocusAfterLostFocus =
            event.target?.classList.contains("ssw-input") && !event.relatedTarget;

        setTimeout(() => {
            if (!focusRef.current && refocusAfterLostFocus) {
                // will only execute if the focus was lost to an unexpected action
                setFocus(true);
            }
        }, 500);
    }

    function handleOnBlurFallback(): void {
        setTimeout(() => {
            if (focusRef.current) {
                // will only execute when the input lost focus, but the flyout was not hidden
                setFocus(false);
            }
        }, 500);
    }

    function closeFlyoutTriggeredByTabNavigation(): void {
        shell?.messageBroker.publishTo("ESMKT.HeadR.CloseFlyout", null);
    }

    const sanatizeInput = (input: string) => {
        const validInput = input.length > SEARCH_TERM_LENGTH_LIMIT ? input.slice(0, 50) : input;
        return validInput;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value ?? "";
        const sanatizedInput = sanatizeInput(value);
        setInput(sanatizedInput);

        const trimmedInput = sanatizedInput.trim();
        if (!trimmedInput) {
            debouncedFetch.current.cancel();
            setValidSearchTerm("");
            resetSuggestionResponse();
        } else {
            debouncedFetch.current(trimmedInput);
        }
    };

    const debouncedFetch = useRef<((input: string) => void) & { cancel: () => void }>(
        debounce((input: string) => {
            setValidSearchTerm(input);
            fetchSuggestions(input);
        }, 250)
    );

    const fetchSuggestions = async (input: string) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const headers: Record<string, string> = pageViewLogDataId
            ? { "X-PageView-ID": pageViewLogDataId }
            : {};

        try {
            const res = await fetch(
                `${props.apiUrl}suggestion/?searchTerm=${encodeURIComponent(
                    input
                )}&topSearchQueriesLimit=${TOP_SEARCH_QUERIES_LIMIT}`,
                { headers, signal: controller.signal }
            );

            if (!res.ok) throw new Error("Network response was not ok");

            const response = await res.json();

            if (
                response.articles.length ||
                response.flexibleSets.length ||
                response.contents.length ||
                response.topSearchQueries.length
            ) {
                setSuggestions(response);
            } else {
                resetSuggestionResponse();
            }
        } catch (error) {
            if (error instanceof Error && error.name !== "AbortError") {
                resetSuggestionResponse();
            }
        } finally {
            abortControllerRef.current = null;
        }
    };

    const resetSuggestionResponse = () => {
        setSuggestions(defaultSuggestionResponse());
    };

    return (
        <L10nContext.Provider value={l10n}>
            <div
                className={
                    "searchbox flyout" +
                    ` ${isOpen ? "open" : ""}` +
                    ` ${focus ? "focus" : ""}` +
                    ` ${props.tenant}`
                }
                ref={searchBoxRef}
            >
                <div className="search">
                    <form
                        method="get"
                        action={props.searchTarget}
                        acceptCharset="utf-8"
                        onSubmit={handleSubmit}
                    >
                        <input
                            ref={inputRef}
                            className="ssw-input"
                            type="text"
                            name="query"
                            aria-label={l10n.defaultTerm}
                            maxLength={50}
                            value={input}
                            onChange={handleChange}
                            onFocus={handleOnFocusFallback}
                            onBlur={handleOnBlurFallback}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                        />
                        <button className="ssw-button" type="submit" aria-label={l10n.defaultTerm}>
                            <SearchIcon />
                        </button>
                    </form>
                </div>
                {showSuggestions && (
                    <div id={`new-suggestions-${props.view}`} className={"new-suggestions"}>
                        {showColumns ? (
                            <NavigationContext.Provider value={navigationApi}>
                                <SuggestionColumns
                                    navigationRef={keyboardNavColumnElementRef}
                                    portal={props.portal}
                                    culture={props.culture}
                                    searchTarget={props.searchTarget}
                                    trackingEndpoint={props.trackingEndpoint}
                                    articles={suggestions.articles}
                                    flexibleSets={suggestions.flexibleSets}
                                    contents={suggestions.contents}
                                    featurePromotion={suggestions.featurePromotion}
                                    topSearchQueries={suggestions.topSearchQueries}
                                    priceMandantSettings={suggestions.priceMandantSettingsArticles}
                                    priceMandantSettingsV2={
                                        suggestions.priceMandantSettingsFlexibleSet
                                    }
                                    customerType={customerType}
                                    searchTerm={validSearchTerm}
                                    isMobileShop={isMobileShop}
                                    view={props.view}
                                    pageViewLogDataId={pageViewLogDataId}
                                    deviceScrollClass={DeviceScrollEnum.None}
                                    deviceType={device}
                                />
                            </NavigationContext.Provider>
                        ) : (
                            <NavigationContext.Provider value={navigationApi}>
                                <SuggestionList
                                    navigationRef={keyboardNavListElementRef}
                                    portal={props.portal}
                                    culture={props.culture}
                                    searchTarget={props.searchTarget}
                                    trackingEndpoint={props.trackingEndpoint}
                                    articles={suggestions.articles}
                                    flexibleSets={suggestions.flexibleSets}
                                    contents={suggestions.contents}
                                    featurePromotion={suggestions.featurePromotion}
                                    topSearchQueries={suggestions.topSearchQueries}
                                    priceMandantSettings={suggestions.priceMandantSettingsArticles}
                                    priceMandantSettingsV2={
                                        suggestions.priceMandantSettingsFlexibleSet
                                    }
                                    customerType={customerType}
                                    searchTerm={validSearchTerm}
                                    isMobileShop={isMobileShop}
                                    view={props.view}
                                    pageViewLogDataId={pageViewLogDataId}
                                    deviceType={device}
                                />
                            </NavigationContext.Provider>
                        )}
                    </div>
                )}
            </div>
        </L10nContext.Provider>
    );
};

export default SearchBoxFlyout;
